import AppComponent from '../base/app-component.ts';
import type { DataTable, ColumnDef } from './data-table.ts';
import { raccogliDateFilters, handleDateOpChange, resetDateRanges } from '../lib/date-filter.ts';
import { getCasseMap } from '../lib/casse-info.ts';

const COLUMNS: ColumnDef[] = [
  { key: 'lavoratore.cognome', label: 'Lavoratore', type: 'link', href: '/lavoratori/{{lavoratoreId}}', text: '{{lavoratore.cognome}} {{lavoratore.nome}}', sortable: true },
  { key: 'lavoratore.codiceFiscale', label: 'CF', type: 'code', sortable: true },
  { key: 'dataNascitaLavoratore', label: 'Data nascita', type: 'date', sortable: true, emptyText: '-' },
  { key: 'telefonoLabel', label: 'Telefono', type: 'text', emptyText: '-' },
  { key: 'cassa', label: 'Cassa', type: 'text', sortable: true },
  { key: 'stato', label: 'Stato', type: 'text', sortable: true },
  { key: 'createdAt', label: 'Data', type: 'date', sortable: true },
  { key: 'note', label: 'Note', type: 'text' },
];

export class ElencoDeleghe extends AppComponent {
  static override componentName = 'wc-elenco-deleghe';
  protected override get templateId() { return 'elenco-deleghe'; }

  private dataTable: DataTable | null = null;
  private currentSort: string | null = null;
  private currentSortDir: 'asc' | 'desc' | null = null;
  private currentFilter = '';
  private currentPage = 1;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private offcanvas: any = null;
  private messageListenerKey: symbol | null = null;

  protected override buildRenderingData(): Record<string, unknown> {
    return {
      ...super.buildRenderingData(),
      columnsJson: JSON.stringify(COLUMNS),
      casse: Object.values(getCasseMap()),
    };
  }

  protected override afterRender(): void {
    this.dataTable = this.$('wc-data-table') as DataTable | null;
    if (!this.dataTable) return;

    this.setupTableEvents();
    this.setupFiltriAvanzati();
    this.setupFiltriRapidi();
    this.setupFiltroArchivio();
    this.setupExportExcel();
    this.setupMessageListener();
    this.caricaDeleghe();
  }

  private setupTableEvents(): void {
    if (!this.dataTable) return;

    this.listen(this.dataTable, 'dt-page-change', (e: Event) => {
      this.currentPage = (e as CustomEvent).detail.page;
      this.caricaDeleghe();
    });

    this.listen(this.dataTable, 'dt-sort-change', (e: Event) => {
      const { sort, dir } = (e as CustomEvent).detail;
      this.currentSort = sort;
      this.currentSortDir = dir;
      this.currentPage = 1;
      this.caricaDeleghe();
    });

    this.listen(this.dataTable, 'dt-filter-change', (e: Event) => {
      this.currentFilter = (e as CustomEvent).detail.filter;
      this.currentPage = 1;
      this.caricaDeleghe();
    });

    this.listen(this.dataTable, 'dt-row-action', (e: Event) => {
      const { action, row } = (e as CustomEvent).detail;
      if (action === 'modifica') this.handleModifica(row);
    });
  }

  private setupFiltriAvanzati(): void {
    const offcanvasEl = this.$('[data-offcanvas="filtri"]');
    if (offcanvasEl) {
      this.offcanvas = new (globalThis as any).bootstrap.Offcanvas(offcanvasEl);
    }

    this.delegate.on('click', '[data-action="apri-filtri"]', () => {
      this.offcanvas?.show();
    });

    const form = this.$('[data-form="filtri-avanzati"]') as HTMLFormElement | null;
    if (form) {
      this.listen(form, 'submit', (e: Event) => {
        e.preventDefault();
        this.offcanvas?.hide();
        this.syncButtonGroup('cassa');
        this.syncButtonGroup('stato');
        this.currentPage = 1;
        this.caricaDeleghe();
      });
    }

    this.delegate.on('click', '[data-action="reset-filtri"], [data-action="reset-filtri-toolbar"]', () => {
      this.resetFiltri();
      this.offcanvas?.hide();
      this.currentPage = 1;
      this.caricaDeleghe();
    });

    this.delegate.on('change', '[data-date-op]', (e: Event) => handleDateOpChange(this, e));
  }

  private setupFiltriRapidi(): void {
    this.delegate.on('click', '[data-filtro-rapido] button', (e: Event) => {
      const btn = (e.target as HTMLElement).closest('button') as HTMLElement;
      const group = btn.closest('[data-filtro-rapido]') as HTMLElement;
      const campo = group.dataset.filtroRapido!;
      const value = btn.dataset.value!;
      const wasActive = btn.classList.contains('active');

      group.querySelectorAll('button').forEach(b => b.classList.remove('active'));
      if (!wasActive) btn.classList.add('active');

      const select = this.$(`[data-filtro-val="${campo}"]`) as HTMLSelectElement | null;
      if (select) select.value = wasActive ? '' : value;

      this.currentPage = 1;
      this.caricaDeleghe();
    });
  }

  private setupFiltroArchivio(): void {
    this.delegate.on('change', '[data-filtro-da-archivio]', () => {
      this.currentPage = 1;
      this.caricaDeleghe();
    });
  }

  private setupExportExcel(): void {
    this.delegate.on('click', '[data-action="export-excel"]', (e: Event) => {
      e.preventDefault();
      const filtri = this.raccogliFiltri();
      const params = new URLSearchParams();
      for (const [k, v] of Object.entries(filtri)) {
        if (v) params.set(k, v);
      }
      if (this.currentFilter) params.set('q', this.currentFilter);
      window.open(`/api/deleghe/export-csv?${params.toString()}`, '_blank');
    });
  }

  private setupMessageListener(): void {
    const handler = (e: MessageEvent) => {
      if (e.data?.action === 'modal-close' && e.data.refresh) {
        this.caricaDeleghe();
      }
    };
    this.messageListenerKey = this.listen(window, 'message', handler as EventListener);
  }

  private syncButtonGroup(campo: string): void {
    const select = this.$(`[data-filtro-val="${campo}"]`) as HTMLSelectElement | null;
    const group = this.$(`[data-filtro-rapido="${campo}"]`);
    if (!select || !group) return;
    const val = select.value;
    group.querySelectorAll('button').forEach(b => {
      const btn = b as HTMLElement;
      btn.classList.toggle('active', btn.dataset.value === val);
    });
  }

  private raccogliFiltri(): Record<string, string> {
    const params: Record<string, string> = {};

    for (const campo of ['cassa', 'stato']) {
      const val = (this.$(`[data-filtro-val="${campo}"]`) as HTMLSelectElement | null)?.value;
      if (val) params[campo] = val;
    }

    const da = (this.$('[data-filtro-val="da"]') as HTMLInputElement | null)?.value;
    const a = (this.$('[data-filtro-val="a"]') as HTMLInputElement | null)?.value;
    if (da) params.da = da;
    if (a) params.a = a;

    Object.assign(params, raccogliDateFilters(this, ['dataNascita']));

    const note = (this.$('[data-filtro-val="note"]') as HTMLInputElement | null)?.value;
    if (note) params.note = note;

    const archivioCheck = this.$('[data-filtro-da-archivio]') as HTMLInputElement | null;
    if (archivioCheck?.checked) params.da_archivio = '1';

    return params;
  }

  private resetFiltri(): void {
    const form = this.$('[data-form="filtri-avanzati"]') as HTMLFormElement | null;
    form?.reset();
    resetDateRanges(this);
    this.syncButtonGroup('cassa');
    this.syncButtonGroup('stato');
    const archivioCheck = this.$('[data-filtro-da-archivio]') as HTMLInputElement | null;
    if (archivioCheck) archivioCheck.checked = false;
  }

  private updateFiltriCount(): void {
    const filtri = this.raccogliFiltri();
    const count = Object.keys(filtri).length;
    const badge = this.$('[data-filtri-count]');
    if (badge) {
      badge.textContent = count > 0 ? String(count) : '';
      badge.classList.toggle('d-none', count === 0);
    }
    const resetBtn = this.$('[data-action="reset-filtri-toolbar"]');
    resetBtn?.classList.toggle('d-none', count === 0);
  }

  private async caricaDeleghe(): Promise<void> {
    if (!this.dataTable) return;
    this.dataTable.setLoading();

    const filtri = this.raccogliFiltri();
    this.updateFiltriCount();

    const result = await this.api.deleghe.lista({
      ...filtri,
      page: this.currentPage,
      limit: 30,
      sort: this.currentSort ?? undefined,
      sortDir: this.currentSortDir ?? undefined,
      q: this.currentFilter || undefined,
    });

    if (!result.ok) {
      this.dataTable.setError(result.error);
      return;
    }

    const { deleghe, count, totalPages, pageSize, page } = result.data;
    const rows = deleghe.map(d => {
      const raw = d as Record<string, unknown>;
      const lav = raw.lavoratore as Record<string, unknown> | null;
      return {
        ...raw,
        telefonoLabel: lav ? (lav.cellulare || lav.telefono || null) : null,
      };
    });
    this.dataTable.setData(rows, {
      page, totalPages, count, pageSize,
    });
  }

  private async handleModifica(row: Record<string, unknown>): Promise<void> {
    const id = row.id as number;
    const result = await Weext.modalIframe(`/deleghe/${id}/modifica?layout=modal`, {
      title: 'Modifica delega',
      size: 'lg',
    });
    if (result.refresh) {
      this.caricaDeleghe();
    }
  }

  protected override onDisconnected(): void {
    this.offcanvas?.dispose();
    if (this.messageListenerKey) {
      this.unlisten(this.messageListenerKey);
      this.messageListenerKey = null;
    }
  }
}
